<template>
    <div class="w-full max-w-225 self-center">
        <BaseNotification v-if="error" theme="error" :text="error" @dismiss="error = null" />
        <ChatMessageControls
            :is-disabled="isTranscribing"
            :is-transcribing="isTranscribing"
            :document-upload-enabled="documentUploadEnabled"
            :default-text-input="defaultTextInput"
            :is-sending="isSending"
            :placeholder="placeholder"
            :location="CHAT_MESSAGE_CONTROLS_LOCATION.HOMEPAGE"
            :pii-patterns="piiPatterns"
            :better-voice-composer-enabled="betterVoiceComposerEnabled"
            :has-workiq-connected="hasWorkiqConnected"
            @text-send="handleTextSend"
            @text-keydown="error = null"
            @recording-complete="handleRecordingComplete"
            @upload-error="handleUploadError" />
    </div>
</template>

<script setup lang="ts">
import { progress, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { GlobalProperties, UploadedFile } from "~vue/appTypes";
import ChatMessageControls from "~vue/ChatDetail/ChatMessageControls.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import { showFullPageLoading } from "~vue/components/FullPageLoading.vue";
import { useChatInputPreference } from "~vue/composables/useChatInputPreference.js";
import { type AudioStats, useTranscription } from "~vue/composables/useTranscription";
import { useChatStore } from "~vue/stores/chatStore";
import { isInIFrame } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHAT_MESSAGE_CONTROLS_LOCATION, CHATSOURCE } from "~vue/utils/constants.js";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import type { PatternEntry } from "~vue/utils/pii/keywordClassifier";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { getWafErrorMessage, WAF_ERROR_CAUSE } from "~vue/utils/wafDetection";
import { computed, inject, ref } from "vue";

type PastedContent = { name: string; content: string; description: string };
type InitialMessageFile = UploadedFile | PastedContent;

withDefaults(
    defineProps<{
        documentUploadEnabled: boolean;
        defaultChatRedirectUrl: string;
        placeholder?: string;
        piiPatterns?: PatternEntry[] | null;
        betterVoiceComposerEnabled?: boolean;
        hasWorkiqConnected?: boolean;
    }>(),
    {
        placeholder: "What's on your mind?",
        betterVoiceComposerEnabled: false,
        hasWorkiqConnected: false,
    },
);

const userChatInputPreference = useChatInputPreference();
const defaultTextInput = computed(() => {
    return userChatInputPreference.value !== null ? userChatInputPreference.value : true;
});

const { $feedbackMailto } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);

const isSending = ref(false);
const error = ref<string | null>(null);
const { isTranscribing, transcribe } = useTranscription({ source: "ChatHome", error });

// Setup API for POST request to start chat
const startChatPayload = ref<{
    chat_source: string;
    initial_message: string;
    initial_message_files: InitialMessageFile[];
} | null>(null);
const {
    data: startChatResponse,
    execute: executeStartChat,
    error: startChatError,
    isFetching: isStartingChat,
} = useFetch("/coach/start/", { immediate: false }).post(startChatPayload).json();

const handleTextSend = async (textSendArgs: { content: string; source?: string }) => {
    const { content, source = "text" } = textSendArgs;
    const hasFiles = useChatStore.files.length > 0;
    // `isSending` is being used as a lock here to prevent creating multiple coaching sessions from
    // double-clicks on the submit button.
    if ((!content?.trim() && !hasFiles) || isSending.value || isStartingChat.value) {
        return;
    }
    isSending.value = true;
    error.value = null;

    // Generate a unique user coach exchange ID for tracking this interaction
    const uce_id = crypto.randomUUID();

    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CHAT_STARTED, { source });
    logUserInteraction(AmplitudeEventNames.USER_MESSAGE_SENT, {
        uce_id,
        mic: source === "voice",
        chat_source: CHATSOURCE.HOME_CHAT_MESSAGE,
    });

    try {
        // Show top loading indicator
        progress.start();

        // Prepare POST payload (sensitive data in body, not URL).
        // Files in the store are a mix of UploadedFile (from /api/chat/document/upload)
        // and pasted-content temp objects (from ChatMessageControls.handlePaste).
        // The backend FileModel union accepts both shapes verbatim.
        const initialMessageFiles: InitialMessageFile[] = (useChatStore.files as InitialMessageFile[]).filter((f) => "id" in f || "content" in f);
        startChatPayload.value = {
            chat_source: CHATSOURCE.HOME_CHAT_MESSAGE,
            initial_message: content,
            initial_message_files: initialMessageFiles,
        };

        await executeStartChat();

        if (startChatError.value) {
            throw startChatError.value;
        }

        if (startChatResponse.value?.redirect_url) {
            useChatStore.removeAllFiles();

            // Navigate to chat page (Inertia will show its own progress during navigation)
            if (isInIFrame()) {
                showFullPageLoading();
                globalThis.location.href = startChatResponse.value.redirect_url;
            } else {
                router.visit(startChatResponse.value.redirect_url);
            }
        } else {
            throw new Error("No redirect URL received");
        }
    } catch (e) {
        // The session-ended dialog is already up and is modal, so a banner behind it would only
        // contradict it. Not logged either -- an expired session is not a defect (ENG-1667).
        if (isSessionEndedError(e)) {
            isSending.value = false;
            return;
        }
        if (e instanceof Error && e.cause === WAF_ERROR_CAUSE) {
            error.value = getWafErrorMessage($feedbackMailto);
        } else {
            error.value = e instanceof Error && e.message ? e.message : "Something went wrong. Please try again.";
        }
        logError(e);
        isSending.value = false;
    } finally {
        // Always finish the POST request progress indicator
        progress.finish();
    }
};

const handleRecordingComplete = async ({ blob, audioStats }: { blob: Blob; audioStats?: AudioStats & { initToStartMs?: number } }) => {
    const transcript = await transcribe({ blob, audioStats });
    if (transcript) {
        handleTextSend({ content: transcript, source: "voice" });
    }
};

const handleUploadError = (message: string | null) => {
    error.value = message;
};
</script>
